import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WhiteboardCanvas } from '../components/whiteboard/WhiteboardCanvas';
import { WhiteboardToolbar } from '../components/whiteboard/WhiteboardToolbar';
import { WhiteboardContextMenu } from '../components/whiteboard/WhiteboardContextMenu';
import { SelectionPropertiesPanel } from '../components/whiteboard/SelectionPropertiesPanel';
import { ShareModal } from '../components/whiteboard/ShareModal';
import { TemplatesModal } from '../components/whiteboard/TemplatesModal';
import { Minimap } from '../components/whiteboard/Minimap';
import { CommentMarker } from '../components/whiteboard/CommentMarker';
import { CommentThread } from '../components/whiteboard/CommentThread';
import { CommentsPanel } from '../components/whiteboard/CommentsPanel';
import { VersionHistoryModal } from '../components/whiteboard/VersionHistoryModal';
import { ChatPanel } from '../components/whiteboard/ChatPanel';
import { useWebSocket } from '../hooks/useWebSocket';
import { ArrowLeft, Users, Share2, MoreHorizontal, Save, Loader2, Check, Cloud, CloudOff, X, Maximize, Download, Image as ImageIcon, Wifi, WifiOff, LayoutGrid, MessageCircle, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const generateId = () => `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper to calculate anchor point position for a shape
const getAnchorPosition = (obj, side) => {
  if (!obj || !side) return null;
  
  switch (side) {
    case 'top':
      return { x: obj.x + obj.width / 2, y: obj.y };
    case 'right':
      return { x: obj.x + obj.width, y: obj.y + obj.height / 2 };
    case 'bottom':
      return { x: obj.x + obj.width / 2, y: obj.y + obj.height };
    case 'left':
      return { x: obj.x, y: obj.y + obj.height / 2 };
    default:
      return null;
  }
};

// Update connectors when a shape moves
const updateConnectedConnectors = (objects, movedObjectId, newX, newY, newWidth, newHeight) => {
  return objects.map(obj => {
    if (obj.type !== 'arrow') return obj;
    
    let updates = {};
    
    // Check if this connector starts from the moved object
    if (obj.startObjectId === movedObjectId) {
      const movedObj = { x: newX, y: newY, width: newWidth, height: newHeight };
      const newStart = getAnchorPosition(movedObj, obj.startSide);
      if (newStart) {
        updates.startX = newStart.x;
        updates.startY = newStart.y;
      }
    }
    
    // Check if this connector ends at the moved object
    if (obj.endObjectId === movedObjectId) {
      const movedObj = { x: newX, y: newY, width: newWidth, height: newHeight };
      const newEnd = getAnchorPosition(movedObj, obj.endSide);
      if (newEnd) {
        updates.endX = newEnd.x;
        updates.endY = newEnd.y;
      }
    }
    
    // If there are updates, also recalculate bounding box
    if (Object.keys(updates).length > 0) {
      const startX = updates.startX ?? obj.startX;
      const startY = updates.startY ?? obj.startY;
      const endX = updates.endX ?? obj.endX;
      const endY = updates.endY ?? obj.endY;
      const startSide = obj.startSide;
      const endSide = obj.endSide;
      const connectorType = obj.connectorType;
      
      // Calculate bounding box that accounts for elbow/curved paths
      const boundingBox = calculateConnectorBoundingBox(
        startX, startY, endX, endY, 
        startSide, endSide, connectorType
      );
      
      return {
        ...obj,
        ...updates,
        x: boundingBox.x,
        y: boundingBox.y,
        width: boundingBox.width,
        height: boundingBox.height,
      };
    }
    
    return obj;
  });
};

// Calculate proper bounding box for connector paths
const calculateConnectorBoundingBox = (startX, startY, endX, endY, startSide, endSide, connectorType) => {
  const OFFSET = 40; // Same offset used in rendering
  const PADDING = 20; // Extra padding for selection/rendering
  
  // Start with the basic min/max
  let minX = Math.min(startX, endX);
  let minY = Math.min(startY, endY);
  let maxX = Math.max(startX, endX);
  let maxY = Math.max(startY, endY);
  
  // For elbow connectors, account for the offset segments
  if (connectorType === 'elbow' && startSide && endSide) {
    // Account for exit offset from start
    if (startSide === 'right') {
      maxX = Math.max(maxX, startX + OFFSET);
    } else if (startSide === 'left') {
      minX = Math.min(minX, startX - OFFSET);
    } else if (startSide === 'bottom') {
      maxY = Math.max(maxY, startY + OFFSET);
    } else if (startSide === 'top') {
      minY = Math.min(minY, startY - OFFSET);
    }
    
    // Account for approach offset to end
    if (endSide === 'left') {
      minX = Math.min(minX, endX - OFFSET);
    } else if (endSide === 'right') {
      maxX = Math.max(maxX, endX + OFFSET);
    } else if (endSide === 'top') {
      minY = Math.min(minY, endY - OFFSET);
    } else if (endSide === 'bottom') {
      maxY = Math.max(maxY, endY + OFFSET);
    }
  }
  
  // For curved connectors, account for control points
  if (connectorType === 'curved') {
    const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
    const curvature = Math.min(distance * 0.4, 100);
    
    if (startSide === 'right') {
      maxX = Math.max(maxX, startX + curvature);
    } else if (startSide === 'left') {
      minX = Math.min(minX, startX - curvature);
    } else if (startSide === 'bottom') {
      maxY = Math.max(maxY, startY + curvature);
    } else if (startSide === 'top') {
      minY = Math.min(minY, startY - curvature);
    }
    
    if (endSide === 'left') {
      minX = Math.min(minX, endX - curvature);
    } else if (endSide === 'right') {
      maxX = Math.max(maxX, endX + curvature);
    } else if (endSide === 'top') {
      minY = Math.min(minY, endY - curvature);
    } else if (endSide === 'bottom') {
      maxY = Math.max(maxY, endY + curvature);
    }
  }
  
  return {
    x: minX - PADDING,
    y: minY - PADDING,
    width: (maxX - minX) + PADDING * 2,
    height: (maxY - minY) + PADDING * 2,
  };
};

export const WhiteboardPage = () => {
  const { id: whiteboardId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Canvas state
  const [objects, setObjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTool, setActiveTool] = useState('select');
  const [connectorType, setConnectorType] = useState('elbow'); // 'straight', 'elbow', 'curved'
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [gridType, setGridType] = useState('dots'); // 'dots', 'grid', 'none'
  const [snapToGrid, setSnapToGrid] = useState(true);
  
  // Pen/Drawing settings
  const [penSettings, setPenSettings] = useState({
    color: '#1E293B',
    width: 2,
    isHighlighter: false,
    pressureSensitive: true // speed-based width
  });
  
  // Shape settings
  const [shapeSettings, setShapeSettings] = useState({
    fillColor: '#FFFFFF',
    strokeColor: '#1E293B',
    strokeWidth: 2,
    fillOpacity: 1, // 1 = solid, 0.5 = semi-transparent, 0 = transparent
    fontSize: 14,
    textColor: '#1E293B'
  });

  // Text tool settings
  const [textSettings, setTextSettings] = useState({
    fontSize: 16,
    textColor: '#1E293B',
    fontWeight: 'normal'
  });

  // Sticky note settings
  const [stickySettings, setStickySettings] = useState({
    textColor: '#1E293B'
  });
  
  // Context menu
  const [contextMenu, setContextMenu] = useState(null);
  
  // Present mode
  const [isPresentMode, setIsPresentMode] = useState(false);
  
  // Share modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  // Templates modal
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [selectedComment, setSelectedComment] = useState(null);
  const [isCommentsPanelOpen, setIsCommentsPanelOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  
  // Version history state
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  
  // Chat state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  
  // History for undo/redo - use refs to avoid closure issues
  const historyRef = useRef([[]]);
  const historyIndexRef = useRef(0);
  const [, forceUpdate] = useState(0);
  const maxHistorySize = 50; // Limit history to prevent memory issues
  
  // LocalStorage key for history persistence
  const getHistoryKey = useCallback(() => {
    const boardId = currentWhiteboardId.current || whiteboardId;
    return boardId && boardId !== 'new' ? `boardchat_history_${boardId}` : null;
  }, [whiteboardId]);
  
  // Save history to localStorage (debounced)
  const saveHistoryToStorage = useCallback(() => {
    const key = getHistoryKey();
    if (!key) return;
    
    try {
      const historyData = {
        history: historyRef.current.slice(-maxHistorySize), // Keep last N entries
        index: Math.min(historyIndexRef.current, maxHistorySize - 1),
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(historyData));
    } catch (error) {
      console.warn('Failed to save history to localStorage:', error);
      // If storage is full, clear old histories
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k?.startsWith('boardchat_history_') && k !== key) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
      } catch (e) {
        console.warn('Failed to clear old histories:', e);
      }
    }
  }, [getHistoryKey]);
  
  // Load history from localStorage on mount
  useEffect(() => {
    const key = getHistoryKey();
    if (!key) return;
    
    try {
      const savedHistory = localStorage.getItem(key);
      if (savedHistory) {
        const { history, index, timestamp } = JSON.parse(savedHistory);
        // Only restore if less than 24 hours old
        if (timestamp && Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          historyRef.current = history;
          historyIndexRef.current = index;
          forceUpdate(n => n + 1);
        } else {
          // Clear old history
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn('Failed to load history from localStorage:', error);
    }
  }, [getHistoryKey]);
  
  // Whiteboard name and persistence state
  const [boardName, setBoardName] = useState('Untitled Whiteboard');
  const [actualBoardId, setActualBoardId] = useState(whiteboardId !== 'new' ? whiteboardId : null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveTimeoutRef = useRef(null);
  const currentWhiteboardId = useRef(whiteboardId);
  
  // Ref to track if update is from WebSocket (to prevent echo)
  const isRemoteUpdateRef = useRef(false);

  // WebSocket for real-time collaboration
  const {
    isConnected,
    users,
    remoteCursors,
    remoteSelections,
    setHandlers,
    sendCursorMove,
    sendSelectionChanged,
    sendObjectCreated,
    sendObjectUpdated,
    sendObjectDeleted,
    sendObjectsBatchUpdate,
  } = useWebSocket(
    whiteboardId && whiteboardId !== 'new' ? whiteboardId : null,
    token,
    user,
    !isLoading && whiteboardId && whiteboardId !== 'new'
  );

  // Send selection changes to collaborators
  useEffect(() => {
    if (isConnected) {
      sendSelectionChanged(selectedIds);
    }
  }, [selectedIds, isConnected, sendSelectionChanged]);

  // Load whiteboard data
  useEffect(() => {
    const loadWhiteboard = async () => {
      if (!whiteboardId || whiteboardId === 'new') {
        // Create new whiteboard
        setIsLoading(false);
        setObjects([]);
        setBoardName('Untitled Whiteboard');
        setSaveStatus('unsaved');
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setObjects(data.objects || []);
          setBoardName(data.name);
          historyRef.current = [data.objects || []];
          historyIndexRef.current = 0;
          currentWhiteboardId.current = data.id;
          setSaveStatus('saved');
        } else if (response.status === 404) {
          // Whiteboard not found, create new
          toast.error('Whiteboard not found');
          navigate('/whiteboard/new');
        }
      } catch (error) {
        console.error('Error loading whiteboard:', error);
        toast.error('Failed to load whiteboard');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      loadWhiteboard();
    }
  }, [whiteboardId, token, navigate]);

  // Reload whiteboard data (used after applying templates)
  const reloadWhiteboard = useCallback(async () => {
    // Use currentWhiteboardId.current which is updated after save
    const boardId = currentWhiteboardId.current;
    if (!boardId || boardId === 'new' || !token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setObjects(data.objects || []);
        historyRef.current = [data.objects || []];
        historyIndexRef.current = 0;
        setSaveStatus('saved');
      }
    } catch (error) {
      console.error('Error reloading whiteboard:', error);
    }
  }, [token]);

  // Fetch comments for the whiteboard
  const fetchComments = useCallback(async () => {
    const boardId = currentWhiteboardId.current;
    if (!boardId || boardId === 'new' || !token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  }, [token]);

  // Fetch comments when whiteboard loads
  useEffect(() => {
    if (!isLoading && whiteboardId && whiteboardId !== 'new') {
      fetchComments();
    }
  }, [isLoading, whiteboardId, fetchComments]);

  // Update collaborators list from connected users
  useEffect(() => {
    const collabs = users.map(u => ({
      id: u.id,
      name: u.name || u.email?.split('@')[0] || 'Anonymous',
      email: u.email || ''
    }));
    setCollaborators(collabs);
  }, [users]);

  // Set up WebSocket event handlers
  useEffect(() => {
    setHandlers({
      onObjectCreated: (object, userId) => {
        // Don't process our own events
        if (userId === user?.id) return;
        
        isRemoteUpdateRef.current = true;
        setObjects(prev => {
          // Check if object already exists (avoid duplicates)
          if (prev.find(o => o.id === object.id)) return prev;
          return [...prev, object];
        });
        isRemoteUpdateRef.current = false;
      },
      onObjectUpdated: (objectId, changes, userId) => {
        if (userId === user?.id) return;
        
        isRemoteUpdateRef.current = true;
        setObjects(prev => prev.map(obj =>
          obj.id === objectId ? { ...obj, ...changes } : obj
        ));
        isRemoteUpdateRef.current = false;
      },
      onObjectDeleted: (objectIds, userId) => {
        if (userId === user?.id) return;
        
        isRemoteUpdateRef.current = true;
        setObjects(prev => prev.filter(obj => !objectIds.includes(obj.id)));
        isRemoteUpdateRef.current = false;
      },
      onObjectsBatchUpdate: (updatedObjects, userId) => {
        if (userId === user?.id) return;
        
        isRemoteUpdateRef.current = true;
        setObjects(prev => {
          const updated = [...prev];
          for (const obj of updatedObjects) {
            const index = updated.findIndex(o => o.id === obj.id);
            if (index !== -1) {
              updated[index] = { ...updated[index], ...obj };
            }
          }
          return updated;
        });
        isRemoteUpdateRef.current = false;
      },
      // Comment handlers
      onCommentCreated: (comment, userId) => {
        if (userId === user?.id) return;
        setComments(prev => {
          if (prev.find(c => c.id === comment.id)) return prev;
          return [...prev, comment];
        });
      },
      onCommentUpdated: (comment, userId) => {
        if (userId === user?.id) return;
        setComments(prev => prev.map(c => c.id === comment.id ? comment : c));
        // Update selected comment if it's the one being updated
        setSelectedComment(prev => prev?.id === comment.id ? comment : prev);
      },
      onCommentDeleted: (commentId, userId) => {
        if (userId === user?.id) return;
        setComments(prev => prev.filter(c => c.id !== commentId));
        // Close thread if this comment was selected
        setSelectedComment(prev => prev?.id === commentId ? null : prev);
      },
      onCommentReplyAdded: (comment, userId) => {
        if (userId === user?.id) return;
        setComments(prev => prev.map(c => c.id === comment.id ? comment : c));
        setSelectedComment(prev => prev?.id === comment.id ? comment : prev);
      },
      onCommentReaction: (comment, userId) => {
        if (userId === user?.id) return;
        setComments(prev => prev.map(c => c.id === comment.id ? comment : c));
        setSelectedComment(prev => prev?.id === comment.id ? comment : prev);
      },
      // Chat message handler
      onNewMessage: (message, userId) => {
        if (userId === user?.id) return;
        setChatMessages(prev => {
          if (prev.find(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        // Increment unread count if chat is closed
        if (!isChatOpen) {
          setUnreadChatCount(prev => prev + 1);
        }
      },
    });
  }, [setHandlers, user?.id, isChatOpen]);

  // Auto-save with debounce
  const triggerAutoSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setHasUnsavedChanges(true);
    setSaveStatus('unsaved');
    
    saveTimeoutRef.current = setTimeout(() => {
      saveWhiteboard();
    }, 2000); // Auto-save after 2 seconds of inactivity
  }, []);

  // Save whiteboard to backend
  const saveWhiteboard = useCallback(async () => {
    if (!token) return;
    
    setIsSaving(true);
    setSaveStatus('saving');

    try {
      let response;
      const boardData = {
        name: boardName,
        objects: objects
      };

      if (currentWhiteboardId.current && currentWhiteboardId.current !== 'new') {
        // Update existing whiteboard
        response = await fetch(`${BACKEND_URL}/api/whiteboards/${currentWhiteboardId.current}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(boardData)
        });
      } else {
        // Create new whiteboard
        response = await fetch(`${BACKEND_URL}/api/whiteboards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name: boardName })
        });

        if (response.ok) {
          const newBoard = await response.json();
          currentWhiteboardId.current = newBoard.id;
          setActualBoardId(newBoard.id); // Update state to trigger re-render
          // Update URL without full page reload
          window.history.replaceState(null, '', `/whiteboard/${newBoard.id}`);
          
          // Now save the objects
          await fetch(`${BACKEND_URL}/api/whiteboards/${newBoard.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ objects: objects })
          });
        }
      }

      if (response.ok) {
        setSaveStatus('saved');
        setHasUnsavedChanges(false);
        return currentWhiteboardId.current; // Return the ID
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving whiteboard:', error);
      setSaveStatus('error');
      toast.error('Failed to save whiteboard');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [token, boardName, objects]);

  // Save and return the whiteboard ID (for templates)
  const saveAndGetId = useCallback(async () => {
    const savedId = await saveWhiteboard();
    return savedId;
  }, [saveWhiteboard]);

  // Manual save handler
  const handleManualSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveWhiteboard();
  }, [saveWhiteboard]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Save to history and trigger auto-save
  const saveToHistory = useCallback((newObjects) => {
    const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push([...newObjects]);
    
    // Limit history size
    if (newHistory.length > maxHistorySize) {
      newHistory.shift();
    }
    
    historyRef.current = newHistory;
    historyIndexRef.current = newHistory.length - 1;
    forceUpdate(n => n + 1);
    triggerAutoSave();
    
    // Persist to localStorage (debounced)
    saveHistoryToStorage();
  }, [triggerAutoSave, saveHistoryToStorage]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      setObjects([...historyRef.current[historyIndexRef.current]]);
      forceUpdate(n => n + 1);
      triggerAutoSave();
      saveHistoryToStorage();
    }
  }, [triggerAutoSave, saveHistoryToStorage]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      setObjects([...historyRef.current[historyIndexRef.current]]);
      forceUpdate(n => n + 1);
      triggerAutoSave();
      saveHistoryToStorage();
    }
  }, [triggerAutoSave, saveHistoryToStorage]);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  // Add object
  const addObject = useCallback((type, position, options = {}) => {
    const newObject = {
      id: generateId(),
      type,
      x: position.x,
      y: position.y,
      width: options.width || (type === 'sticky' ? 200 : type === 'text' ? 150 : 120),
      height: options.height || (type === 'sticky' ? 200 : type === 'text' ? 40 : 80),
      rotation: 0,
      zIndex: objects.length,
      locked: false,
      ...options,
      // Type-specific defaults
      ...(type === 'sticky' && { 
        color: options.color || '#FEF3C7', 
        text: options.text || '',
        textColor: options.textColor || stickySettings.textColor
      }),
      ...(type === 'text' && { 
        text: options.text || 'Text', 
        fontSize: options.fontSize || textSettings.fontSize,
        fontWeight: options.fontWeight || textSettings.fontWeight,
        color: options.color || textSettings.textColor
      }),
      ...(type === 'shape' && { 
        shape: options.shape || 'rectangle',
        fill: options.fill || shapeSettings.fillColor,
        stroke: options.stroke || shapeSettings.strokeColor,
        strokeWidth: options.strokeWidth || shapeSettings.strokeWidth,
        fillOpacity: options.fillOpacity ?? shapeSettings.fillOpacity,
        text: options.text || '',
        fontSize: options.fontSize || shapeSettings.fontSize,
        textColor: options.textColor || shapeSettings.textColor
      }),
      ...(type === 'frame' && {
        title: options.title || 'Frame',
        fill: 'transparent',
        stroke: '#94A3B8',
        strokeWidth: 1
      }),
      ...(type === 'image' && {
        src: options.src || '',
      }),
      ...(type === 'drawing' && {
        points: options.points || [],
        stroke: options.stroke || '#1E293B',
        strokeWidth: options.strokeWidth || 2
      }),
    };
    
    const newObjects = [...objects, newObject];
    setObjects(newObjects);
    saveToHistory(newObjects);
    setSelectedIds([newObject.id]);
    
    // Emit WebSocket event for real-time collaboration
    if (!isRemoteUpdateRef.current) {
      sendObjectCreated(newObject);
    }
    
    return newObject;
  }, [objects, saveToHistory, shapeSettings, textSettings, stickySettings, sendObjectCreated]);

  // Update object (and update connected connectors)
  const updateObject = useCallback((id, updates) => {
    // First, update the target object
    let newObjects = objects.map(obj => 
      obj.id === id ? { ...obj, ...updates } : obj
    );
    
    // If the object has position/size updates, update connected connectors
    if (updates.x !== undefined || updates.y !== undefined || 
        updates.width !== undefined || updates.height !== undefined) {
      const targetObj = newObjects.find(o => o.id === id);
      if (targetObj && targetObj.type !== 'arrow') {
        newObjects = updateConnectedConnectors(
          newObjects, 
          id, 
          targetObj.x, 
          targetObj.y, 
          targetObj.width, 
          targetObj.height
        );
      }
    }
    
    setObjects(newObjects);
    
    // Emit WebSocket event (throttled - updateObject is called during drag)
    if (!isRemoteUpdateRef.current) {
      sendObjectUpdated(id, updates);
    }
  }, [objects, sendObjectUpdated]);

  // Update objects and save to history (and update connected connectors)
  const updateObjectWithHistory = useCallback((id, updates) => {
    // First, update the target object
    let newObjects = objects.map(obj => 
      obj.id === id ? { ...obj, ...updates } : obj
    );
    
    // If the object has position/size updates, update connected connectors
    if (updates.x !== undefined || updates.y !== undefined || 
        updates.width !== undefined || updates.height !== undefined) {
      const targetObj = newObjects.find(o => o.id === id);
      if (targetObj && targetObj.type !== 'arrow') {
        newObjects = updateConnectedConnectors(
          newObjects, 
          id, 
          targetObj.x, 
          targetObj.y, 
          targetObj.width, 
          targetObj.height
        );
      }
    }
    
    setObjects(newObjects);
    saveToHistory(newObjects);
    
    // Emit WebSocket event for real-time collaboration
    if (!isRemoteUpdateRef.current) {
      sendObjectUpdated(id, updates);
    }
  }, [objects, saveToHistory, sendObjectUpdated]);

  // Delete objects
  const deleteObjects = useCallback((ids) => {
    const newObjects = objects.filter(obj => !ids.includes(obj.id));
    setObjects(newObjects);
    saveToHistory(newObjects);
    setSelectedIds([]);
    
    // Emit WebSocket event for real-time collaboration
    if (!isRemoteUpdateRef.current) {
      sendObjectDeleted(ids);
    }
  }, [objects, saveToHistory, sendObjectDeleted]);

  // Duplicate objects
  const duplicateObjects = useCallback((ids) => {
    const toDuplicate = objects.filter(obj => ids.includes(obj.id));
    const duplicated = toDuplicate.map(obj => ({
      ...obj,
      id: generateId(),
      x: obj.x + 20,
      y: obj.y + 20,
      zIndex: objects.length
    }));
    const newObjects = [...objects, ...duplicated];
    setObjects(newObjects);
    saveToHistory(newObjects);
    setSelectedIds(duplicated.map(o => o.id));
  }, [objects, saveToHistory]);

  // ========== COPY/PASTE CLIPBOARD ==========
  const clipboardRef = useRef([]);
  
  const copyObjects = useCallback((ids) => {
    const toCopy = objects.filter(obj => ids.includes(obj.id));
    if (toCopy.length === 0) return;
    
    clipboardRef.current = toCopy.map(obj => ({ ...obj }));
    toast.success(`Copied ${toCopy.length} object${toCopy.length > 1 ? 's' : ''}`);
  }, [objects]);

  const pasteObjects = useCallback(() => {
    if (clipboardRef.current.length === 0) {
      toast.error('Nothing to paste');
      return;
    }
    
    const pasted = clipboardRef.current.map(obj => ({
      ...obj,
      id: generateId(),
      x: obj.x + 30,
      y: obj.y + 30,
      zIndex: objects.length,
      groupId: undefined // Remove group association on paste
    }));
    
    const newObjects = [...objects, ...pasted];
    setObjects(newObjects);
    saveToHistory(newObjects);
    setSelectedIds(pasted.map(o => o.id));
    
    // Update clipboard positions for next paste
    clipboardRef.current = clipboardRef.current.map(obj => ({
      ...obj,
      x: obj.x + 30,
      y: obj.y + 30
    }));
    
    toast.success(`Pasted ${pasted.length} object${pasted.length > 1 ? 's' : ''}`);
    
    // Emit WebSocket events for real-time collaboration
    if (!isRemoteUpdateRef.current) {
      pasted.forEach(obj => sendObjectCreated(obj));
    }
  }, [objects, saveToHistory, sendObjectCreated]);

  // ========== LOCK/UNLOCK OBJECTS ==========
  const toggleLock = useCallback((ids) => {
    const toToggle = objects.filter(obj => ids.includes(obj.id));
    if (toToggle.length === 0) return;
    
    // If any is unlocked, lock all. Otherwise unlock all.
    const shouldLock = toToggle.some(obj => !obj.locked);
    
    const newObjects = objects.map(obj => 
      ids.includes(obj.id) ? { ...obj, locked: shouldLock } : obj
    );
    setObjects(newObjects);
    saveToHistory(newObjects);
    
    toast.success(shouldLock ? 'Objects locked' : 'Objects unlocked');
    
    // Emit WebSocket events
    if (!isRemoteUpdateRef.current) {
      ids.forEach(id => sendObjectUpdated(id, { locked: shouldLock }));
    }
  }, [objects, saveToHistory, sendObjectUpdated]);

  // Update multiple objects at once (for properties panel)
  const updateMultipleObjects = useCallback((ids, updates) => {
    const newObjects = objects.map(obj => 
      ids.includes(obj.id) ? { ...obj, ...updates } : obj
    );
    setObjects(newObjects);
    saveToHistory(newObjects);
    
    // Emit WebSocket events for real-time collaboration
    if (!isRemoteUpdateRef.current) {
      ids.forEach(id => sendObjectUpdated(id, updates));
    }
  }, [objects, saveToHistory, sendObjectUpdated]);

  // Bring to front / Send to back
  const bringToFront = useCallback((ids) => {
    const maxZ = Math.max(...objects.map(o => o.zIndex));
    const newObjects = objects.map(obj => 
      ids.includes(obj.id) ? { ...obj, zIndex: maxZ + 1 } : obj
    );
    setObjects(newObjects);
    saveToHistory(newObjects);
  }, [objects, saveToHistory]);

  const sendToBack = useCallback((ids) => {
    const minZ = Math.min(...objects.map(o => o.zIndex));
    const newObjects = objects.map(obj => 
      ids.includes(obj.id) ? { ...obj, zIndex: minZ - 1 } : obj
    );
    setObjects(newObjects);
    saveToHistory(newObjects);
  }, [objects, saveToHistory]);

  // Group objects
  const groupObjects = useCallback((ids) => {
    if (ids.length < 2) return;
    
    const toGroup = objects.filter(obj => ids.includes(obj.id));
    const minX = Math.min(...toGroup.map(o => o.x));
    const minY = Math.min(...toGroup.map(o => o.y));
    const maxX = Math.max(...toGroup.map(o => o.x + o.width));
    const maxY = Math.max(...toGroup.map(o => o.y + o.height));
    
    const groupId = generateId();
    const newObjects = objects.map(obj => 
      ids.includes(obj.id) ? { ...obj, groupId } : obj
    );
    
    setObjects(newObjects);
    saveToHistory(newObjects);
    toast.success(`Grouped ${ids.length} objects`);
  }, [objects, saveToHistory]);

  // Ungroup objects
  const ungroupObjects = useCallback((ids) => {
    const toUngroup = objects.filter(obj => ids.includes(obj.id) && obj.groupId);
    if (toUngroup.length === 0) return;
    
    // Get all unique group IDs from selected objects
    const groupIds = [...new Set(toUngroup.map(o => o.groupId))];
    
    // Remove groupId from all objects in those groups
    const newObjects = objects.map(obj => 
      groupIds.includes(obj.groupId) ? { ...obj, groupId: undefined } : obj
    );
    
    setObjects(newObjects);
    saveToHistory(newObjects);
    toast.success('Objects ungrouped');
  }, [objects, saveToHistory]);

  // ========== ALIGNMENT TOOLS ==========
  const alignObjects = useCallback((ids, alignment) => {
    if (ids.length < 2) return;
    
    const toAlign = objects.filter(obj => ids.includes(obj.id));
    
    let newObjects = [...objects];
    
    switch (alignment) {
      case 'left': {
        const minX = Math.min(...toAlign.map(o => o.x));
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, x: minX } : obj
        );
        break;
      }
      case 'center': {
        const minX = Math.min(...toAlign.map(o => o.x));
        const maxX = Math.max(...toAlign.map(o => o.x + o.width));
        const centerX = (minX + maxX) / 2;
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, x: centerX - obj.width / 2 } : obj
        );
        break;
      }
      case 'right': {
        const maxX = Math.max(...toAlign.map(o => o.x + o.width));
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, x: maxX - obj.width } : obj
        );
        break;
      }
      case 'top': {
        const minY = Math.min(...toAlign.map(o => o.y));
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, y: minY } : obj
        );
        break;
      }
      case 'middle': {
        const minY = Math.min(...toAlign.map(o => o.y));
        const maxY = Math.max(...toAlign.map(o => o.y + o.height));
        const centerY = (minY + maxY) / 2;
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, y: centerY - obj.height / 2 } : obj
        );
        break;
      }
      case 'bottom': {
        const maxY = Math.max(...toAlign.map(o => o.y + o.height));
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, y: maxY - obj.height } : obj
        );
        break;
      }
      case 'distribute-h': {
        if (ids.length < 3) return;
        const sorted = [...toAlign].sort((a, b) => a.x - b.x);
        const totalWidth = sorted.reduce((sum, o) => sum + o.width, 0);
        const minX = sorted[0].x;
        const maxX = sorted[sorted.length - 1].x + sorted[sorted.length - 1].width;
        const spacing = (maxX - minX - totalWidth) / (sorted.length - 1);
        
        let currentX = minX;
        const positions = {};
        sorted.forEach((obj, i) => {
          positions[obj.id] = currentX;
          currentX += obj.width + spacing;
        });
        
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, x: positions[obj.id] } : obj
        );
        break;
      }
      case 'distribute-v': {
        if (ids.length < 3) return;
        const sorted = [...toAlign].sort((a, b) => a.y - b.y);
        const totalHeight = sorted.reduce((sum, o) => sum + o.height, 0);
        const minY = sorted[0].y;
        const maxY = sorted[sorted.length - 1].y + sorted[sorted.length - 1].height;
        const spacing = (maxY - minY - totalHeight) / (sorted.length - 1);
        
        let currentY = minY;
        const positions = {};
        sorted.forEach((obj, i) => {
          positions[obj.id] = currentY;
          currentY += obj.height + spacing;
        });
        
        newObjects = objects.map(obj => 
          ids.includes(obj.id) ? { ...obj, y: positions[obj.id] } : obj
        );
        break;
      }
      default:
        return;
    }
    
    setObjects(newObjects);
    saveToHistory(newObjects);
    
    // Emit WebSocket batch update for aligned objects
    if (!isRemoteUpdateRef.current) {
      const updatedObjs = newObjects.filter(obj => ids.includes(obj.id));
      sendObjectsBatchUpdate(updatedObjs);
    }
  }, [objects, saveToHistory, sendObjectsBatchUpdate]);

  // ========== COMMENTS FUNCTIONALITY ==========
  const createComment = useCallback(async (position) => {
    const boardId = currentWhiteboardId.current;
    if (!boardId || boardId === 'new' || !token) {
      toast.error('Please save the whiteboard first to add comments');
      return;
    }

    // Show a prompt for comment content
    const content = window.prompt('Enter your comment:');
    if (!content || !content.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: content.trim(),
          x: position.x,
          y: position.y,
          mentions: []
        })
      });

      if (response.ok) {
        const newComment = await response.json();
        setComments(prev => [...prev, newComment]);
        setSelectedComment(newComment);
        setActiveTool('select');
        toast.success('Comment added');
      } else {
        throw new Error('Failed to create comment');
      }
    } catch (error) {
      console.error('Error creating comment:', error);
      toast.error('Failed to add comment');
    }
  }, [token, setActiveTool]);

  const handleCommentReply = useCallback(async (commentId, content, mentions = []) => {
    if (!token) return;

    try {
      const boardId = currentWhiteboardId.current;
      if (!boardId) return;
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments/${commentId}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content, mentions })
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c));
        setSelectedComment(updatedComment);
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to add reply');
    }
  }, [token]);

  const handleCommentResolve = useCallback(async (commentId, resolved) => {
    if (!token) return;

    try {
      const boardId = currentWhiteboardId.current;
      if (!boardId) return;
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ resolved })
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c));
        setSelectedComment(updatedComment);
        toast.success(resolved ? 'Comment resolved' : 'Comment reopened');
      }
    } catch (error) {
      console.error('Error resolving comment:', error);
      toast.error('Failed to update comment');
    }
  }, [token]);

  const handleCommentDelete = useCallback(async (commentId) => {
    if (!token) return;

    try {
      const boardId = currentWhiteboardId.current;
      if (!boardId) return;
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
        setSelectedComment(null);
        toast.success('Comment deleted');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  }, [token]);

  const handleCommentReaction = useCallback(async (commentId, emoji) => {
    if (!token) return;

    try {
      const boardId = currentWhiteboardId.current;
      if (!boardId) return;
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments/${commentId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c));
        setSelectedComment(prev => prev?.id === commentId ? updatedComment : prev);
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  }, [token]);

  const handleReplyReaction = useCallback(async (commentId, replyId, emoji) => {
    if (!token) return;

    try {
      const boardId = currentWhiteboardId.current;
      if (!boardId) return;
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${boardId}/comments/${commentId}/replies/${replyId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c));
        setSelectedComment(prev => prev?.id === commentId ? updatedComment : prev);
      }
    } catch (error) {
      console.error('Error adding reaction to reply:', error);
    }
  }, [token]);

  const navigateToComment = useCallback((comment) => {
    // Center the view on the comment position
    const canvasWidth = window.innerWidth - 72; // Minus toolbar width
    const canvasHeight = window.innerHeight - 56; // Minus header height
    
    const newPanX = (canvasWidth / 2) - (comment.x * zoom);
    const newPanY = (canvasHeight / 2) - (comment.y * zoom);
    
    setPan({ x: newPanX, y: newPanY });
    setSelectedComment(comment);
    setIsCommentsPanelOpen(false);
  }, [zoom, setPan]);

  // Handle click on canvas for comment tool
  const handleCanvasCommentClick = useCallback((e) => {
    if (activeTool !== 'comment') return;
    
    // Get click position relative to canvas (accounting for pan and zoom)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;
    
    createComment({ x, y });
  }, [activeTool, pan, zoom, createComment]);

  // ========== IMAGE UPLOAD ==========
  const addImageToCanvas = useCallback((imageData, position = null) => {
    const img = new Image();
    img.onload = () => {
      // Scale down large images
      let width = img.width;
      let height = img.height;
      const maxSize = 400;
      
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width *= ratio;
        height *= ratio;
      }
      
      const x = position?.x ?? (-pan.x + window.innerWidth / 2) / zoom - width / 2;
      const y = position?.y ?? (-pan.y + window.innerHeight / 2) / zoom - height / 2;
      
      addObject('image', { x, y }, {
        width,
        height,
        src: imageData
      });
      
      toast.success('Image added to canvas');
    };
    img.onerror = () => {
      toast.error('Failed to load image');
    };
    img.src = imageData;
  }, [addObject, pan, zoom]);

  // Handle paste (Ctrl+V for images)
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              addImageToCanvas(event.target.result);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };
    
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [addImageToCanvas]);

  // Handle image file upload from button
  const handleImageUpload = useCallback((file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      addImageToCanvas(event.target.result);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  }, [addImageToCanvas]);

  // Handle file drop
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported');
      return;
    }
    
    // Get drop position relative to canvas
    const canvasRect = e.currentTarget.getBoundingClientRect();
    const dropX = (e.clientX - canvasRect.left - pan.x) / zoom;
    const dropY = (e.clientY - canvasRect.top - pan.y) / zoom;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      addImageToCanvas(event.target.result, { x: dropX, y: dropY });
    };
    reader.readAsDataURL(file);
  }, [addImageToCanvas, pan, zoom]);

  // ========== EXPORT FUNCTIONALITY ==========
  const exportAsImage = useCallback(async (format = 'png') => {
    try {
      toast.loading('Generating export...', { id: 'export' });
      
      // Create a temporary SVG element with all objects
      const svgNS = 'http://www.w3.org/2000/svg';
      
      // Calculate bounds of all objects
      if (objects.length === 0) {
        toast.error('Nothing to export', { id: 'export' });
        return;
      }
      
      const padding = 50;
      const minX = Math.min(...objects.map(o => o.x)) - padding;
      const minY = Math.min(...objects.map(o => o.y)) - padding;
      const maxX = Math.max(...objects.map(o => o.x + o.width)) + padding;
      const maxY = Math.max(...objects.map(o => o.y + o.height)) + padding;
      const width = maxX - minX;
      const height = maxY - minY;
      
      // Get the SVG content from the canvas
      const canvasSvg = document.querySelector('[data-testid="whiteboard-canvas"] svg');
      if (!canvasSvg) {
        toast.error('Canvas not found', { id: 'export' });
        return;
      }
      
      // Clone the SVG and adjust viewBox
      const svgClone = canvasSvg.cloneNode(true);
      svgClone.setAttribute('viewBox', `${minX} ${minY} ${width} ${height}`);
      svgClone.setAttribute('width', width);
      svgClone.setAttribute('height', height);
      
      // Remove grid pattern for cleaner export
      const gridRect = svgClone.querySelector('rect[fill^="url"]');
      if (gridRect) {
        gridRect.setAttribute('fill', 'white');
      }
      
      // Serialize SVG
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      
      if (format === 'svg') {
        // Download as SVG
        const url = URL.createObjectURL(svgBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${boardName || 'whiteboard'}.svg`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('SVG exported!', { id: 'export' });
        return;
      }
      
      // Convert to PNG/JPEG using canvas
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width * 2; // 2x for higher quality
        canvas.height = height * 2;
        const ctx = canvas.getContext('2d');
        
        // White background for JPEG
        if (format === 'jpeg' || format === 'jpg') {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        
        ctx.scale(2, 2);
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${boardName || 'whiteboard'}.${format}`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success(`${format.toUpperCase()} exported!`, { id: 'export' });
        }, `image/${format === 'jpg' ? 'jpeg' : format}`);
      };
      
      img.src = URL.createObjectURL(svgBlob);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed', { id: 'export' });
    }
  }, [objects, boardName]);

  // Quick add shape at center of viewport
  const addShapeAtCenter = useCallback((type, options = {}) => {
    const centerX = (-pan.x + window.innerWidth / 2) / zoom;
    const centerY = (-pan.y + window.innerHeight / 2) / zoom;
    addObject(type, { x: centerX - 60, y: centerY - 40 }, options);
  }, [pan, zoom, addObject]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if typing in input/textarea
      const isTyping = document.activeElement.tagName === 'INPUT' || 
                       document.activeElement.tagName === 'TEXTAREA';
      
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdKey = isMac ? e.metaKey : e.ctrlKey;
      
      // Command/Ctrl shortcuts
      if (cmdKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (cmdKey && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      } else if (cmdKey && e.key === 'c' && !e.shiftKey) {
        // Ctrl+C to copy
        e.preventDefault();
        if (selectedIds.length > 0) copyObjects(selectedIds);
      } else if (cmdKey && e.key === 'v' && !e.shiftKey) {
        // Ctrl+V to paste (only if not pasting image - handled by handlePaste)
        if (clipboardRef.current.length > 0) {
          e.preventDefault();
          pasteObjects();
        }
      } else if (cmdKey && e.key === 'd') {
        e.preventDefault();
        if (selectedIds.length > 0) duplicateObjects(selectedIds);
      } else if (cmdKey && e.key === 'l' && !e.shiftKey) {
        // Ctrl+L to lock/unlock
        e.preventDefault();
        if (selectedIds.length > 0) toggleLock(selectedIds);
      } else if (cmdKey && e.shiftKey && e.key === 'g') {
        // Ctrl+Shift+G to ungroup
        e.preventDefault();
        if (selectedIds.length > 0) ungroupObjects(selectedIds);
      } else if (cmdKey && e.key === 'g') {
        // Ctrl+G to group
        e.preventDefault();
        if (selectedIds.length > 1) groupObjects(selectedIds);
      } else if (cmdKey && e.key === 's') {
        e.preventDefault();
        handleManualSave();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.length > 0 && !isTyping) {
          // Don't delete locked objects
          const deletableIds = selectedIds.filter(id => {
            const obj = objects.find(o => o.id === id);
            return obj && !obj.locked;
          });
          if (deletableIds.length > 0) {
            e.preventDefault();
            deleteObjects(deletableIds);
          } else if (selectedIds.length > 0) {
            toast.error('Cannot delete locked objects');
          }
        }
      } else if (e.key === 'Escape') {
        if (isPresentMode) {
          setIsPresentMode(false);
        } else {
          setSelectedIds([]);
          setActiveTool('select');
        }
      }
      
      // Single key shortcuts (only when not typing)
      if (!isTyping && !cmdKey) {
        switch (e.key.toLowerCase()) {
          case 'v':
            setActiveTool('select');
            break;
          case 'h':
            setActiveTool('hand');
            break;
          case 'r':
            setActiveTool('shape-rectangle');
            break;
          case 'o':
            setActiveTool('shape-circle');
            break;
          case 'd':
            if (!cmdKey) setActiveTool('shape-diamond');
            break;
          case 's':
            if (!cmdKey) setActiveTool('sticky-yellow');
            break;
          case 't':
            setActiveTool('text');
            break;
          case 'l':
            setActiveTool('connector');
            break;
          case 'p':
            setActiveTool('pen');
            break;
          case 'f':
            setActiveTool('frame');
            break;
          case 'i':
            // Trigger image upload
            document.getElementById('image-upload-input')?.click();
            break;
          case 'e':
            setActiveTool('eraser');
            break;
          case 'c':
            setActiveTool('comment');
            break;
          case 'n':
            // Quick add shape at center
            e.preventDefault();
            addShapeAtCenter('shape', { shape: 'rectangle' });
            break;
          case '1':
            setConnectorType('straight');
            break;
          case '2':
            setConnectorType('elbow');
            break;
          case '3':
            setConnectorType('curved');
            break;
          case '+':
          case '=':
            setZoom(prev => Math.min(3, prev + 0.25));
            break;
          case '-':
            setZoom(prev => Math.max(0.25, prev - 0.25));
            break;
          case '0':
            setZoom(1);
            setPan({ x: 0, y: 0 });
            break;
          default:
            break;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, duplicateObjects, groupObjects, ungroupObjects, deleteObjects, selectedIds, handleManualSave, isPresentMode, addShapeAtCenter, setConnectorType, setZoom, setPan]);

  // Context menu handler
  const handleContextMenu = useCallback((e, objectId) => {
    e.preventDefault();
    
    // If right-clicking on an object that's not selected, select it
    if (objectId && !selectedIds.includes(objectId)) {
      setSelectedIds([objectId]);
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        objectIds: [objectId]
      });
    } else if (objectId && selectedIds.includes(objectId)) {
      // Right-clicked on a selected object - use all selected
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        objectIds: selectedIds
      });
    } else if (!objectId && selectedIds.length > 0) {
      // Right-clicked on canvas but have selected objects - use selection
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        objectIds: selectedIds
      });
    } else {
      // Right-clicked on empty canvas with nothing selected
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        objectIds: []
      });
    }
  }, [selectedIds]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Handle board name change with auto-save
  const handleBoardNameChange = useCallback((e) => {
    setBoardName(e.target.value);
    triggerAutoSave();
  }, [triggerAutoSave]);

  // Render save status indicator
  const renderSaveStatus = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span>Saving...</span>
          </div>
        );
      case 'saved':
        return (
          <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 text-sm">
            <Cloud size={14} />
            <span>Saved</span>
          </div>
        );
      case 'unsaved':
        return (
          <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-sm">
            <CloudOff size={14} />
            <span>Unsaved</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 text-sm">
            <CloudOff size={14} />
            <span>Save failed</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#F8FAFC] dark:bg-[#0F172A]" data-testid="whiteboard-loading">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin text-blue-600 dark:text-blue-400" />
          <p className="text-gray-500 dark:text-gray-400">Loading whiteboard...</p>
        </div>
      </div>
    );
  }

  // Present Mode - Clean fullscreen view
  if (isPresentMode) {
    return (
      <div className="h-screen w-screen bg-white dark:bg-[#0F172A] overflow-hidden" data-testid="whiteboard-present-mode">
        {/* Exit button - top right */}
        <Button
          variant="ghost"
          size="sm"
          className="fixed top-4 right-4 z-50 bg-black/50 hover:bg-black/70 text-white"
          onClick={() => setIsPresentMode(false)}
        >
          <X size={18} className="mr-1" />
          Exit (Esc)
        </Button>
        
        {/* Board name - top left */}
        <div className="fixed top-4 left-4 z-50 bg-black/50 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
          {boardName}
        </div>
        
        {/* Canvas in present mode - no grid, no toolbars */}
        <WhiteboardCanvas
          objects={objects}
          selectedIds={[]}
          setSelectedIds={() => {}}
          activeTool="select"
          setActiveTool={() => {}}
          connectorType={connectorType}
          zoom={zoom}
          setZoom={setZoom}
          pan={pan}
          setPan={setPan}
          gridType="none"
          snapToGrid={false}
          addObject={() => {}}
          updateObject={() => {}}
          updateObjectWithHistory={() => {}}
          onContextMenu={() => {}}
        />
        
        {/* Zoom indicator - bottom right */}
        <div className="fixed bottom-4 right-4 z-50 bg-black/50 text-white px-3 py-1.5 rounded-lg text-sm">
          {Math.round(zoom * 100)}%
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-[#F8FAFC] dark:bg-[#0F172A] overflow-hidden relative" style={{ zIndex: 0, isolation: 'isolate' }} data-testid="whiteboard-page">
      {/* Header */}
      <header className="h-14 bg-white dark:bg-[#1E293B] border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          <div className="h-6 w-px bg-gray-200 dark:bg-gray-600" />
          <input
            type="text"
            value={boardName}
            onChange={handleBoardNameChange}
            onBlur={handleManualSave}
            className="text-lg font-medium text-gray-900 dark:text-white bg-transparent border-none outline-none focus:ring-0 placeholder-gray-400 dark:placeholder-gray-500"
            data-testid="whiteboard-name"
          />
          <div className="h-6 w-px bg-gray-200 dark:bg-gray-600" />
          {renderSaveStatus()}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            onClick={() => setIsPresentMode(true)}
            data-testid="present-mode-btn"
          >
            <Maximize size={18} className="mr-1" />
            Present
          </Button>
          
          {/* Templates Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-[#64748B]"
            onClick={() => setIsTemplatesModalOpen(true)}
            data-testid="templates-btn"
          >
            <LayoutGrid size={18} className="mr-1" />
            Templates
          </Button>
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="export-btn">
                <Download size={18} className="mr-1" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportAsImage('png')}>
                Export as PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAsImage('jpeg')}>
                Export as JPEG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAsImage('svg')}>
                Export as SVG
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            onClick={handleManualSave}
            disabled={isSaving || saveStatus === 'saved'}
            data-testid="save-button"
          >
            {isSaving ? (
              <Loader2 size={18} className="mr-1 animate-spin" />
            ) : (
              <Save size={18} className="mr-1" />
            )}
            Save
          </Button>
          
          {/* Real-time collaboration status */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700" data-testid="collab-status">
                  {isConnected ? (
                    <Wifi size={16} className="text-green-500 dark:text-green-400" />
                  ) : (
                    <WifiOff size={16} className="text-gray-400 dark:text-gray-500" />
                  )}
                  {users.length > 0 && (
                    <div className="flex -space-x-2">
                      {users.slice(0, 3).map((u, i) => (
                        <div
                          key={u.id || i}
                          className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 flex items-center justify-center text-white text-xs font-medium"
                          style={{ backgroundColor: u.color || '#6366F1' }}
                        >
                          {u.name?.[0]?.toUpperCase() || '?'}
                        </div>
                      ))}
                      {users.length > 3 && (
                        <div className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 text-xs font-medium">
                          +{users.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isConnected ? `${users.length} user${users.length !== 1 ? 's' : ''} online` : 'Connecting...'}</p>
                {users.length > 0 && (
                  <ul className="text-xs mt-1">
                    {users.map((u, i) => (
                      <li key={u.id || i}>{u.name || 'Anonymous'}</li>
                    ))}
                  </ul>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            onClick={() => setIsShareModalOpen(true)}
            disabled={!actualBoardId}
            data-testid="share-button"
          >
            <Share2 size={18} className="mr-1" />
            Share
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            onClick={() => setIsVersionHistoryOpen(true)}
            disabled={!actualBoardId}
            data-testid="version-history-button"
          >
            <History size={18} className="mr-1" />
            History
          </Button>
          <Button variant="ghost" size="sm" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            <MoreHorizontal size={18} />
          </Button>
        </div>
      </header>

      {/* Main Canvas Area */}
      <div 
        className="flex-1 relative"
        onDrop={handleFileDrop}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
      >
        <WhiteboardToolbar 
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          connectorType={connectorType}
          setConnectorType={setConnectorType}
          zoom={zoom}
          setZoom={setZoom}
          gridType={gridType}
          setGridType={setGridType}
          snapToGrid={snapToGrid}
          setSnapToGrid={setSnapToGrid}
          undo={undo}
          redo={redo}
          canUndo={canUndo}
          canRedo={canRedo}
          onPresentMode={() => setIsPresentMode(true)}
          penSettings={penSettings}
          setPenSettings={setPenSettings}
          shapeSettings={shapeSettings}
          setShapeSettings={setShapeSettings}
          textSettings={textSettings}
          setTextSettings={setTextSettings}
          stickySettings={stickySettings}
          setStickySettings={setStickySettings}
          selectedIds={selectedIds}
          onAlign={alignObjects}
          onExport={exportAsImage}
          commentCount={comments.filter(c => !c.resolved).length}
          onToggleCommentsPanel={() => setIsCommentsPanelOpen(!isCommentsPanelOpen)}
          onImageUpload={handleImageUpload}
        />
        
        <WhiteboardCanvas
          objects={objects}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          connectorType={connectorType}
          zoom={zoom}
          setZoom={setZoom}
          pan={pan}
          setPan={setPan}
          gridType={gridType}
          snapToGrid={snapToGrid}
          addObject={addObject}
          updateObject={updateObject}
          updateObjectWithHistory={updateObjectWithHistory}
          onContextMenu={handleContextMenu}
          onCommentClick={createComment}
          penSettings={penSettings}
          shapeSettings={shapeSettings}
          remoteCursors={remoteCursors}
          remoteSelections={remoteSelections}
          onCursorMove={sendCursorMove}
          whiteboardId={currentWhiteboardId.current || whiteboardId}
          currentUserId={user?.id}
          token={token}
          onVoteUpdate={(objectId, votes) => {
            setObjects(prev => prev.map(obj => 
              obj.id === objectId ? { ...obj, votes } : obj
            ));
          }}
        />
        
        {/* Selection Properties Panel - shows when objects are selected */}
        {selectedIds.length > 0 && (() => {
          const selectedObjs = objects.filter(obj => selectedIds.includes(obj.id));
          if (selectedObjs.length === 0) return null;
          
          // Calculate center position of selection
          const minX = Math.min(...selectedObjs.map(o => o.x));
          const maxX = Math.max(...selectedObjs.map(o => o.x + (o.width || 0)));
          const minY = Math.min(...selectedObjs.map(o => o.y));
          
          // Convert to screen coordinates
          const centerX = ((minX + maxX) / 2) * zoom + pan.x;
          const topY = minY * zoom + pan.y;
          
          return (
            <SelectionPropertiesPanel
              selectedObjects={selectedObjs}
              onUpdateObjects={updateMultipleObjects}
              position={{ x: centerX + 72, y: topY + 56 }} // Offset for toolbar width and header
            />
          );
        })()}
        
        {contextMenu && (
          <WhiteboardContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            objectIds={contextMenu.objectIds}
            objects={objects}
            onClose={closeContextMenu}
            onDelete={() => { 
              const deletableIds = contextMenu.objectIds.filter(id => {
                const obj = objects.find(o => o.id === id);
                return obj && !obj.locked;
              });
              if (deletableIds.length > 0) {
                deleteObjects(deletableIds); 
              } else {
                toast.error('Cannot delete locked objects');
              }
              closeContextMenu(); 
            }}
            onDuplicate={() => { duplicateObjects(contextMenu.objectIds); closeContextMenu(); }}
            onCopy={() => { copyObjects(contextMenu.objectIds); closeContextMenu(); }}
            onPaste={() => { pasteObjects(); closeContextMenu(); }}
            clipboardHasContent={clipboardRef.current.length > 0}
            onBringToFront={() => { bringToFront(contextMenu.objectIds); closeContextMenu(); }}
            onSendToBack={() => { sendToBack(contextMenu.objectIds); closeContextMenu(); }}
            onGroup={() => { groupObjects(contextMenu.objectIds); closeContextMenu(); }}
            onUngroup={() => { ungroupObjects(contextMenu.objectIds); closeContextMenu(); }}
            onLock={() => { toggleLock(contextMenu.objectIds); closeContextMenu(); }}
            onUnlock={() => { toggleLock(contextMenu.objectIds); closeContextMenu(); }}
            onChangeFillColor={(color) => {
              updateMultipleObjects(contextMenu.objectIds, { fill: color, color: color });
              closeContextMenu();
            }}
            onChangeStrokeColor={(color) => {
              updateMultipleObjects(contextMenu.objectIds, { stroke: color });
              closeContextMenu();
            }}
            onChangeTextColor={(color) => {
              updateMultipleObjects(contextMenu.objectIds, { textColor: color });
              closeContextMenu();
            }}
            onFlipHorizontal={() => {
              // Flip objects horizontally (mirror around center)
              const selectedObjs = objects.filter(obj => contextMenu.objectIds.includes(obj.id));
              if (selectedObjs.length === 0) return;
              const minX = Math.min(...selectedObjs.map(o => o.x));
              const maxX = Math.max(...selectedObjs.map(o => o.x + (o.width || 0)));
              const centerX = (minX + maxX) / 2;
              contextMenu.objectIds.forEach(id => {
                const obj = objects.find(o => o.id === id);
                if (obj) {
                  const newX = centerX - (obj.x + (obj.width || 0) - centerX);
                  updateObject(id, { x: newX });
                }
              });
              saveToHistory(objects);
              closeContextMenu();
            }}
            onFlipVertical={() => {
              // Flip objects vertically (mirror around center)
              const selectedObjs = objects.filter(obj => contextMenu.objectIds.includes(obj.id));
              if (selectedObjs.length === 0) return;
              const minY = Math.min(...selectedObjs.map(o => o.y));
              const maxY = Math.max(...selectedObjs.map(o => o.y + (o.height || 0)));
              const centerY = (minY + maxY) / 2;
              contextMenu.objectIds.forEach(id => {
                const obj = objects.find(o => o.id === id);
                if (obj) {
                  const newY = centerY - (obj.y + (obj.height || 0) - centerY);
                  updateObject(id, { y: newY });
                }
              });
              saveToHistory(objects);
              closeContextMenu();
            }}
          />
        )}
      </div>
      
      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        whiteboardId={actualBoardId || whiteboardId}
        whiteboardName={boardName}
        token={token}
      />
      
      {/* Templates Modal */}
      <TemplatesModal
        isOpen={isTemplatesModalOpen}
        onClose={() => setIsTemplatesModalOpen(false)}
        onApplyTemplate={reloadWhiteboard}
        whiteboardId={actualBoardId || whiteboardId}
        token={token}
        onSaveFirst={saveAndGetId}
      />
      
      {/* Minimap */}
      {!isPresentMode && (
        <Minimap
          objects={objects}
          pan={pan}
          setPan={setPan}
          zoom={zoom}
          canvasWidth={window.innerWidth - 72}
          canvasHeight={window.innerHeight - 56}
        />
      )}
      
      {/* Comment Markers on Canvas */}
      {comments.map(comment => (
        <CommentMarker
          key={comment.id}
          comment={comment}
          zoom={zoom}
          pan={{ x: pan.x + 72, y: pan.y + 56 }} // Offset for toolbar and header
          isSelected={selectedComment?.id === comment.id}
          onClick={(c) => {
            setSelectedComment(c);
          }}
          currentUserId={user?.id}
        />
      ))}
      
      {/* Comment Thread - shows when a comment is selected */}
      {selectedComment && (
        <CommentThread
          comment={selectedComment}
          onClose={() => setSelectedComment(null)}
          onReply={(content, mentions) => handleCommentReply(selectedComment.id, content, mentions)}
          onResolve={(resolved) => handleCommentResolve(selectedComment.id, resolved)}
          onDelete={() => handleCommentDelete(selectedComment.id)}
          onReact={(emoji) => handleCommentReaction(selectedComment.id, emoji)}
          onReplyReact={(replyId, emoji) => handleReplyReaction(selectedComment.id, replyId, emoji)}
          currentUserId={user?.id}
          collaborators={collaborators}
          position={{
            x: Math.min((selectedComment.x * zoom + pan.x + 72) + 40, window.innerWidth - 340),
            y: Math.min((selectedComment.y * zoom + pan.y + 56), window.innerHeight - 520)
          }}
        />
      )}
      
      {/* Comments Panel */}
      <CommentsPanel
        isOpen={isCommentsPanelOpen}
        onClose={() => setIsCommentsPanelOpen(false)}
        comments={comments}
        onSelectComment={(comment) => {
          setSelectedComment(comment);
        }}
        onNavigateToComment={navigateToComment}
        currentUserId={user?.id}
      />
      
      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        whiteboardId={currentWhiteboardId.current || whiteboardId}
        token={token}
        onVersionRestored={() => {
          // Reload the whiteboard after version restore
          reloadWhiteboard();
        }}
      />
      
      {/* Chat Panel */}
      <ChatPanel
        isOpen={isChatOpen}
        onClose={() => {
          setIsChatOpen(!isChatOpen);
          if (!isChatOpen) {
            setUnreadChatCount(0);
          }
        }}
        whiteboardId={currentWhiteboardId.current || whiteboardId}
        token={token}
        currentUserId={user?.id}
        collaborators={collaborators}
      />
    </div>
  );
};
